import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Define colors for each status.
const COLORS = {
  Confirmed: "#22C55E",
  Declined: "#FF4D4F",
  Pending: "#F8CB00",
};

// Style object for the select elements with custom SVG icon from the public folder.
const isArabic = document.documentElement.lang === "ar";

const selectStyle = {
  padding: "6px 16px",
  borderRadius: "8px",
  border: "1px solid var(--Secondry-10, #E9EAEB)",
  color: "var(--Light-text, #6B7280)",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "24px",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  backgroundImage: `url("/Icon-right.svg")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: isArabic ? "left 10px center" : "right 10px center",
  minWidth: "100px",
  outline: "none",
  cursor: "pointer",
  textAlign: isArabic ? "right" : "left",
  direction: isArabic ? "rtl" : "ltr",
};

const BarChartComponent = ({ statistics, monthlyData, onFilterChange }) => {
  const { t } = useTranslation("dashboard");

  // State for filters.
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Map English month names to month indices for translation lookup
  const monthNameToIndex = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  // Function to get translated month name with enhanced debugging
  const getTranslatedMonth = (monthValue) => {
    console.log("Translating month:", monthValue);

    // If it's a number or numeric string (1-12)
    const monthIndex = Number(monthValue);
    if (!isNaN(monthIndex) && monthIndex >= 1 && monthIndex <= 12) {
      const translated = t(`barChart.month${monthIndex}`);
      console.log(`Month ${monthIndex} translated to:`, translated);
      return translated;
    }

    // If it's English month name, convert to index then translate
    const mappedIndex = monthNameToIndex[monthValue];
    if (mappedIndex) {
      const translated = t(`barChart.month${mappedIndex}`);
      console.log(
        `Month ${monthValue} (index ${mappedIndex}) translated to:`,
        translated
      );
      return translated;
    }

    // Try direct translation key matching
    const lowerMonth = String(monthValue).toLowerCase();
    const directTranslationKeys = {
      january: "barChart.month1",
      february: "barChart.month2",
      march: "barChart.month3",
      april: "barChart.month4",
      may: "barChart.month5",
      june: "barChart.month6",
      july: "barChart.month7",
      august: "barChart.month8",
      september: "barChart.month9",
      october: "barChart.month10",
      november: "barChart.month11",
      december: "barChart.month12",
    };

    if (directTranslationKeys[lowerMonth]) {
      const translated = t(directTranslationKeys[lowerMonth]);
      console.log(`Direct translation for ${monthValue}:`, translated);
      return translated;
    }

    // Try partial matching (in case data has abbreviated months)
    const partialMatches = {
      jan: "barChart.month1",
      feb: "barChart.month2",
      mar: "barChart.month3",
      apr: "barChart.month4",
      may: "barChart.month5",
      jun: "barChart.month6",
      jul: "barChart.month7",
      aug: "barChart.month8",
      sep: "barChart.month9",
      oct: "barChart.month10",
      nov: "barChart.month11",
      dec: "barChart.month12",
    };

    const monthStart = lowerMonth.substring(0, 3);
    if (partialMatches[monthStart]) {
      const translated = t(partialMatches[monthStart]);
      console.log(`Partial match for ${monthValue}:`, translated);
      return translated;
    }

    console.warn("Could not translate month:", monthValue);
    // Fallback: return as-is
    return monthValue;
  };

  // Transform the chart data to include translated month names
  const chartData = useMemo(() => {
    console.log("Original monthlyData:", monthlyData);
    console.log("Current language:", t("barChart.month1")); // Test translation

    if (monthlyData && monthlyData.length > 0) {
      const transformedData = monthlyData.map((item, index) => {
        console.log(`Processing item ${index}:`, item);
        const translatedMonth = getTranslatedMonth(item.month);
        console.log(`Month ${item.month} -> ${translatedMonth}`);

        return {
          ...item,
          month: translatedMonth,
          originalMonth: item.month, // Keep original for reference
        };
      });
      console.log("Final transformed data:", transformedData);
      return transformedData;
    } else if (statistics) {
      return [
        {
          month: t("barChart.status.Confirmed"), // Or whatever makes sense for your fallback
          originalMonth: "Confirmed",
          confirmed: statistics.confirmed?.count ?? 0,
          declined: statistics.declined?.count ?? 0,
          pending: statistics.pending?.count ?? 0,
        },
      ];
    }
    return [];
  }, [monthlyData, statistics, t]);

  console.log("Chart data with translations:", chartData);

  if (chartData.length === 0) {
    return <div>{t("barChart.noData")}</div>;
  }

  // Custom legend renderer: displays a circular icon and status label.
  const renderCustomizedLegend = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        marginTop: 10,
        marginBottom: 100,
      }}
    >
      {["Confirmed", "Declined", "Pending"].map((key, index) => (
        <div
          key={`legend-item-${index}`}
          style={{ display: "flex", alignItems: "center", margin: "0 10px", gap: "8px" }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: COLORS[key] || "#000",
              border: "1px solid #ccc",
              marginRight: 8,
            }}
          />
          <div style={{ fontSize: 12, color: "#333", fontWeight: "bold" }}>
            {t(`barChart.status.${key}`)}
          </div>
        </div>
      ))}
    </div>
  );

  // Handlers for filter changes that update the local state and notify the parent.
  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    onFilterChange?.(newYear, selectedMonth);
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    onFilterChange?.(selectedYear, newMonth);
  };

  // Custom tooltip to show translated month names
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: "4px 0", color: entry.color }}>
              {t(
                `barChart.status.${
                  entry.dataKey.charAt(0).toUpperCase() + entry.dataKey.slice(1)
                }`
              )}{" "}
              : {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {/* Static Title and Filters Container */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h3 className="dashboard-subtitle" style={{ margin: 0 }}>
          {t("barChart.statisticsTitle")}
        </h3>
        <div style={{ display: "flex", gap: "10px" }}>
          <select
            id="yearFilter"
            value={selectedYear}
            onChange={handleYearChange}
            style={selectStyle}
          >
            <option value="">{t("barChart.yearPlaceholder")}</option>
            <option value="2020">{t("barChart.year2020")}</option>
            <option value="2021">{t("barChart.year2021")}</option>
            <option value="2022">{t("barChart.year2022")}</option>
            <option value="2023">{t("barChart.year2023")}</option>
            <option value="2024">{t("barChart.year2024")}</option>
            <option value="2025">{t("barChart.year2025")}</option>
          </select>
          <select
            id="monthFilter"
            value={selectedMonth}
            onChange={handleMonthChange}
            style={selectStyle}
          >
            <option value="">{t("barChart.monthPlaceholder")}</option>
            <option value="1">{t("barChart.month1")}</option>
            <option value="2">{t("barChart.month2")}</option>
            <option value="3">{t("barChart.month3")}</option>
            <option value="4">{t("barChart.month4")}</option>
            <option value="5">{t("barChart.month5")}</option>
            <option value="6">{t("barChart.month6")}</option>
            <option value="7">{t("barChart.month7")}</option>
            <option value="8">{t("barChart.month8")}</option>
            <option value="9">{t("barChart.month9")}</option>
            <option value="10">{t("barChart.month10")}</option>
            <option value="11">{t("barChart.month11")}</option>
            <option value="12">{t("barChart.month12")}</option>
          </select>
        </div>
      </div>

      {renderCustomizedLegend()}

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          {/* Only horizontal grid lines */}
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          {/* X-axis now uses the already translated month names */}
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            interval={0}
          />
          <YAxis axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="confirmed" fill={COLORS["Confirmed"]} barSize={10} />
          <Bar dataKey="declined" fill={COLORS["Declined"]} barSize={10} />
          <Bar dataKey="pending" fill={COLORS["Pending"]} barSize={10} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChartComponent;
